import { z } from 'zod'
import { uuidSchema } from '../validators/uuid.validator.js'
import { hcpJobKindSchema } from '../enums/hcp-job-kind.enum.js'
import {
  IRRIGATION_PART_KINDS,
  irrigationPartKindSchema,
} from '../enums/irrigation-part-kind.enum.js'

/** HCP ids look like `pbmcat_<32 hex>`; bounded generously rather than pinned. */
const hcpIdSchema = z.string().trim().min(1).max(100)

/**
 * Changing how Housecall Pro is read.
 *
 * Both fields optional so the screen can save one without restating the
 * other. Choosing a different irrigation category re-reads the copy straight
 * away; a picker's subcategory has to be one of that category's own.
 */
export const updateHousecallProInputSchema = z
  .object({
    irrigationCategoryUuid: hcpIdSchema.nullable().optional(),
    partCategories: z
      .array(
        z
          .object({
            kind: irrigationPartKindSchema,
            categoryUuid: hcpIdSchema.nullable(),
          })
          .strict(),
      )
      .max(IRRIGATION_PART_KINDS.length)
      .refine(
        (entries) =>
          new Set(entries.map((entry) => entry.kind)).size === entries.length,
        'Each picker can be set once.',
      )
      .optional(),
    /** The labor service a residential client's repairs are priced by. */
    residentialLaborServiceUuid: hcpIdSchema.nullable().optional(),
    /** The labor service a commercial client's repairs are priced by. */
    commercialLaborServiceUuid: hcpIdSchema.nullable().optional(),
    /** The service category irrigation drive charges are filed in. */
    driveChargeCategoryId: z.number().int().positive().nullable().optional(),
    /** The service a pruned tree's line on a tree job is made on. */
    treePruningServiceUuid: hcpIdSchema.nullable().optional(),
    /** The service a removed tree's line on a tree job is made on. */
    treeRemovalServiceUuid: hcpIdSchema.nullable().optional(),
  })
  .strict()

export type UpdateHousecallProInput = z.infer<
  typeof updateHousecallProInputSchema
>

/**
 * Switch creating things in Housecall Pro on or off. Its own request, not a
 * field of the settings update, because it is recorded with who made it and
 * no API token may make it.
 */
export const setHcpWritesInputSchema = z
  .object({ enabled: z.boolean() })
  .strict()

export type SetHcpWritesInput = z.infer<typeof setHcpWritesInputSchema>

/** The query a part picker sends. No kind: every irrigation material. */
export const irrigationPartQuerySchema = z
  .object({ kind: irrigationPartKindSchema.optional() })
  .strict()

export type IrrigationPartQuery = z.infer<typeof irrigationPartQuerySchema>

/** Search HCP's customers, by name, email or phone as HCP matches them. */
export const hcpCustomerSearchQuerySchema = z
  .object({ q: z.string().trim().min(2, 'Type at least two letters').max(100) })
  .strict()

export type HcpCustomerSearchQuery = z.infer<typeof hcpCustomerSearchQuerySchema>

/** Link a client to an HCP customer, or unlink it with null. */
export const linkHcpCustomerInputSchema = z
  .object({ customerId: hcpIdSchema.nullable() })
  .strict()

export type LinkHcpCustomerInput = z.infer<typeof linkHcpCustomerInputSchema>

/** Link a site to an address on its client's HCP customer, or unlink it. */
export const linkHcpAddressInputSchema = z
  .object({ addressId: hcpIdSchema.nullable() })
  .strict()

export type LinkHcpAddressInput = z.infer<typeof linkHcpAddressInputSchema>

/** Create one job of an accepted proposal in HCP: which site, which kind. */
export const createHcpJobInputSchema = z
  .object({ siteId: uuidSchema, kind: hcpJobKindSchema })
  .strict()

export type CreateHcpJobInput = z.infer<typeof createHcpJobInputSchema>

/**
 * Settle a job HCP did not confirm: the id of the job a person found in HCP
 * ("job_" and the rest, from the end of its address there), or null when it
 * is not there and can be created again.
 */
export const settleHcpJobInputSchema = z
  .object({
    hcpJobId: hcpIdSchema
      .regex(/^job_[A-Za-z0-9]+$/, 'A Housecall Pro job id starts with job_')
      .nullable(),
  })
  .strict()

export type SettleHcpJobInput = z.infer<typeof settleHcpJobInputSchema>

